import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { Params } from '@angular/router';

/** The one link above the title of a page, to the page it belongs to. */
export interface PageHeaderParent {
  /** A translation key. */
  label: string;
  link: unknown[] | string;
  queryParams?: Params;
}

/**
 * The head of a page: at most one parent link (or a small eyebrow label such
 * as "Admin") above the H1, an optional subtitle and the actions of the page
 * on the right (`<… slot="actions">`). Title, subtitle, eyebrow and parent
 * label are translation keys; text that is no key (a list name) shows as is.
 * An editable title is renamed in place: Enter saves, Escape cancels.
 */
@Component({
  selector: 'app-page-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PageHeaderComponent {
  @Input() title = '';
  @Input() subtitle: string | null = null;
  @Input() eyebrow: string | null = null;
  @Input() parent: PageHeaderParent | null = null;
  /** The title can be renamed in place, e.g. the name of an own list. */
  @Input() editable = false;
  @Output() titleChange = new EventEmitter<string>();
  @ViewChild('titleInput') titleInput?: ElementRef<HTMLInputElement>;

  public editing = false;

  startEditing() {
    this.editing = true;
    // After the input is rendered.
    setTimeout(() => {
      this.titleInput?.nativeElement.focus();
      this.titleInput?.nativeElement.select();
    });
  }

  /** Enter: the new title, if it is not empty and differs from the old one. */
  commit(value: string) {
    const title = value.trim();
    this.editing = false;
    if (title && title !== this.title) {
      this.titleChange.emit(title);
    }
  }

  cancel() {
    this.editing = false;
  }
}
